import { createGlobalStyle } from "styled-components";
import { colors } from "./colors";
import { line, radii, shadow, space } from "./ui";

/**
 * Ant Design, restyled to the product's own visual language: softer radii,
 * hairline borders, tinted shadows and the bank's accent instead of antd blue.
 * Kept in styled-components rather than a plain stylesheet so every rule reads
 * from the palette, which is driven by an environment variable.
 */
const GlobalStyle = createGlobalStyle`
  /* ── Select trigger ─────────────────────────────────────────────── */
  .ant-select:not(.ant-select-customize-input) .ant-select-selector {
    border-radius: ${radii.md}px;
    border-color: ${line.hairline};
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }
  .ant-select:not(.ant-select-disabled):hover .ant-select-selector {
    border-color: ${colors.primary[300]};
  }
  .ant-select-focused:not(.ant-select-disabled).ant-select:not(.ant-select-customize-input)
    .ant-select-selector {
    border-color: ${colors.primary[400]};
    box-shadow: ${shadow.focusRing};
  }
  .ant-select-single .ant-select-selector .ant-select-selection-item {
    font-weight: 500;
    color: ${colors.gray[700]};
  }
  .ant-select-arrow {
    color: ${colors.gray[400]};
    transition: color 0.15s ease;
  }
  .ant-select:hover .ant-select-arrow {
    color: ${colors.gray[600]};
  }

  /* ── Select panel ───────────────────────────────────────────────────
     Football's menu, applied to EVERY select in the app rather than to the
     ones opted in by name. A dropdown is portaled to the end of body, so a
     component cannot style its own; naming them one at a time left whichever
     had not been named yet — the pager's page-size menu, most visibly —
     looking like a different product. */
  .ant-select-dropdown {
    padding: 6px;
    border-radius: ${radii.menu}px;
    border: 1px solid ${line.hairline};
    box-shadow: ${shadow.pop};
  }
  .ant-select-item {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 34px;
    padding: 0 9px;
    border-radius: ${radii.control}px;
    font-size: 12.5px;
    font-weight: 480;
    line-height: 34px;
    color: ${colors.gray[800]};
    transition: background 0.15s ease, color 0.15s ease;
  }
  .ant-select-item-option-active:not(.ant-select-item-option-disabled) {
    background: ${colors.gray[50]};
  }
  /* Marked by weight and a tick, not by a fill: a filled row beside a hovered
     row reads as two hovers. */
  .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
    background: transparent;
    color: ${colors.gray[900]};
    font-weight: 560;
  }
  .ant-select-item-option-disabled {
    opacity: 0.4;
  }
  .ant-select-item-option-content {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .ant-select-item-option-state {
    display: inline-flex;
    align-items: center;
    color: ${colors.primary[500]};
  }
  .ant-select-item-group {
    padding: 8px 9px 4px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${colors.gray[600]};
  }
  /**
   * A select whose rows are a code and a line saying what it means.
   *
   * The row above is one line tall and sets its leading to the whole 34px,
   * which stacked two lines with even space above, between and below them:
   * a list of ten alternating lines, where a reader could not tell whether
   * "Good until you cancel it" belonged to the GTC above it or the FOK
   * below. Opted into by name, through the dropdownClassName on the
   * time-in-force select, because it is a shape and not a default.
   */
  .fina-select-stacked .ant-select-item {
    min-height: 46px;
    padding: 7px 9px;
    line-height: 1.35;
  }

  .ant-select-item-empty {
    padding: 10px 9px;
    font-size: 12.5px;
    color: ${colors.gray[500]};
  }

  /* ── Dropdown menus (row actions, column filters) ───────────────── */
  .ant-dropdown-menu {
    padding: ${space.xs}px;
    border-radius: ${radii.lg}px;
    border: 1px solid ${line.hairline};
    box-shadow: ${shadow.overlay};
  }
  .ant-dropdown-menu-item {
    padding: 2px 6px;
    border-radius: ${radii.md}px;
    transition: background 0.12s ease;
  }
  .ant-dropdown-menu-item:hover {
    background: ${colors.gray[50]};
  }
  .ant-dropdown-menu-item .ant-btn-link {
    height: auto;
    padding: 4px 6px;
    font-weight: 500;
  }

  /* ── Tooltip ────────────────────────────────────────────────────── */
  .ant-tooltip-inner {
    min-height: 0;
    padding: 6px 10px;
    border-radius: ${radii.md}px;
    background: ${colors.gray[900]};
    box-shadow: ${shadow.raised};
    font-size: 12px;
    line-height: 18px;
  }
  .ant-tooltip-arrow-content {
    background: ${colors.gray[900]};
  }

  /* ── Buttons ────────────────────────────────────────────────────── */
  .ant-btn {
    border-radius: ${radii.md}px;
    font-weight: 500;
    transition: background 0.15s ease, border-color 0.15s ease,
      color 0.15s ease, box-shadow 0.15s ease;
  }
  .ant-btn-round {
    border-radius: ${radii.pill}px;
  }
  .ant-btn-primary {
    background: ${colors.primary[500]};
    border-color: ${colors.primary[500]};
    box-shadow: ${shadow.thumb};
  }
  .ant-btn-primary:hover,
  .ant-btn-primary:focus {
    background: ${colors.primary[600]};
    border-color: ${colors.primary[600]};
  }
  .ant-btn:not(.ant-btn-link):not([disabled]):active {
    transform: translateY(1px);
  }
  .ant-btn:focus-visible {
    box-shadow: ${shadow.focusRing};
  }

  /* ── Selection controls ─────────────────────────────────────────── */
  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: ${colors.primary[500]};
    border-color: ${colors.primary[500]};
  }
  .ant-checkbox-wrapper:hover .ant-checkbox-inner,
  .ant-checkbox:hover .ant-checkbox-inner,
  .ant-checkbox-input:focus + .ant-checkbox-inner {
    border-color: ${colors.primary[400]};
  }
  .ant-checkbox-checked::after {
    border-color: ${colors.primary[500]};
  }
  .ant-radio-checked .ant-radio-inner {
    border-color: ${colors.primary[500]};
  }
  .ant-radio-inner::after {
    background-color: ${colors.primary[500]};
  }

  /* ── Column filter panel ────────────────────────────────────────── */
  .ant-table-filter-dropdown {
    border-radius: ${radii.lg}px;
    box-shadow: ${shadow.overlay};
    overflow: hidden;
  }
  .ant-table-filter-dropdown .ant-dropdown-menu {
    border: 0;
    box-shadow: none;
  }
  .ant-table-filter-dropdown-btns {
    border-top-color: ${line.soft};
  }
  .ant-table-filter-trigger {
    border-radius: ${radii.sm}px;
    transition: background 0.12s ease, color 0.12s ease;
  }
  .ant-table-filter-trigger:hover {
    background: ${line.soft};
    color: ${colors.gray[600]};
  }
  .ant-table-filter-trigger.active {
    background: ${colors.primary[15]};
    color: ${colors.primary[500]};
  }

  /* ── Pagination ─────────────────────────────────────────────────── */
  .ant-pagination-item,
  .ant-pagination-prev .ant-pagination-item-link,
  .ant-pagination-next .ant-pagination-item-link {
    border-radius: ${radii.md}px;
    border-color: ${line.hairline};
  }
  .ant-pagination-item-active {
    background: ${colors.primary[15]};
    border-color: ${colors.primary[500]};
  }
  .ant-pagination-item-active a {
    color: ${colors.primary[600]};
  }

  /* ── Empty state and spinner ────────────────────────────────────── */
  .ant-empty-description {
    color: ${colors.gray[500]};
    font-size: 13px;
  }
  .ant-spin-dot-item {
    background-color: ${colors.primary[500]};
  }

  /* ── Chart tooltips ─────────────────────────────────────────────────
     The chart libraries write their tooltip styling as inline styles — a grey
     halo shadow, a 3px radius, a system font stack — so overriding it needs
     !important. Which also means this block, not a chart's own domStyles
     option, is where a chart tooltip is themed: an inline style loses to it.

     Light, and matched to the Ant Design tooltips in index.css: same
     surface, same edge, same corner, same type. A chart's tooltip is a
     tooltip, and there is no reason for the one over a donut to be a
     different object from the one over a card's title. */
  .g2-tooltip {
    padding: 7px 10px !important;
    border: 1px solid ${line.hairline} !important;
    border-radius: ${radii.control}px !important;
    background: ${colors.white} !important;
    box-shadow: ${shadow.overlay} !important;
    color: ${colors.gray[700]} !important;
    font-family: inherit !important;
    font-size: 12.5px !important;
    line-height: 1.45 !important;
  }
  /* A chart that passes no title still gets the element, and with a margin
     on it that is a blank line at the top of the bubble. */
  .g2-tooltip-title:empty {
    display: none !important;
  }
  .g2-tooltip-title {
    /* The full shorthand, not just the bottom. The library writes a
       12px top margin inline, so overriding margin-bottom alone left that
       in place — a blank band across the top of every tooltip that
       actually had a title. */
    margin: 0 0 4px !important;
    padding: 0 !important;
    color: ${colors.gray[500]} !important;
    font-size: 11px !important;
  }
  .g2-tooltip-list-item {
    display: flex !important;
    align-items: center !important;
    gap: 6px !important;
    margin: 0 !important;
    padding: 1px 0 !important;
    color: ${colors.gray[700]} !important;
    list-style: none !important;
  }
  .g2-tooltip-marker {
    width: 7px !important;
    height: 7px !important;
    border: 0 !important;
    border-radius: 50% !important;
  }
  .g2-tooltip-name {
    color: ${colors.gray[500]} !important;
  }
  .g2-tooltip-value {
    margin-left: auto !important;
    padding-left: ${space.md}px !important;
    color: ${colors.gray[900]} !important;
    font-weight: 600 !important;
    font-variant-numeric: tabular-nums;
  }

  /* ── Dialogs ────────────────────────────────────────────────────────
     The chrome a dialog wears, opted into with a fina-dialog class on its
     wrap. CHROME ONLY — the surface, the head, the foot band and the close
     control. It does not set a width, a font size or a button size: a first
     attempt at this did all three, and every dialog whose insides are laid
     out in percentages came out worse than it went in. What is inside a
     dialog belongs to that dialog. */
  /* Centred, always — a property of the dialog rather than something each
     caller has to remember. Top Up reached for the library's Modal directly
     instead of the app's, and the library anchors a dialog near the TOP of
     the window unless told otherwise, so two dialogs opened from the same row
     landed in different places on the screen.

     This is the library's own centring mechanism rather than flexbox: the
     inline-block and the full-height strut keep a dialog taller than the
     window scrollable, which centring it with flex does not. */
  .fina-dialog {
    text-align: center;
  }

  .fina-dialog::before {
    content: "";
    display: inline-block;
    width: 0;
    height: 100%;
    vertical-align: middle;
  }

  .fina-dialog .ant-modal {
    top: 0;
    display: inline-block;
    padding-bottom: 0;
    text-align: left;
    vertical-align: middle;
  }

  /**
   * Bounded by the WINDOW, with the body as the part that gives way.
   *
   * Nothing capped these, so a dialog whose content was taller than the
   * screen simply grew past it. Every one of them puts its actions in the
   * footer slot — correctly — but the footer went down with the rest: the
   * head scrolled off the top of the viewport, the actions sat below the
   * bottom of it, and reaching either meant scrolling the whole dialog
   * inside its own scrim. Around twenty-five dialogs are on this shell, and
   * every one of them had the same fault for the same reason.
   *
   * A column instead: the head and the foot band hold their size, the body
   * takes what is left and scrolls. So the title stays visible while you
   * work and the actions stay where the reader learned they were, which is
   * the entire point of having a foot band. The same three rules the
   * product's own ui/Modal already carries.
   *
   * The strut above still does the centring, and it still lets a dialog
   * that is somehow taller than this scroll rather than clip.
   */
  .fina-dialog .ant-modal-content {
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 48px);
    overflow: hidden;
    border: 1px solid ${line.hairline};
    border-radius: ${radii.modal}px;
    background: ${colors.white};
    box-shadow: ${shadow.pop};
    padding: 0;
  }

  .fina-dialog .ant-modal-header,
  .fina-dialog .ant-modal-footer {
    flex: none;
  }

  .fina-dialog .ant-modal-header {
    padding: 15px 20px;
    border-bottom: 1px solid ${line.hairline};
    border-radius: 0;
    background: ${colors.white};
  }

  /* The title, and whatever wrapper a dialog puts around it. These were set
     in a 1.5em blue serif, centred, with a top margin — a heading with more
     presence than the dialog it names, in a face used nowhere else. */
  .fina-dialog .ant-modal-title,
  .fina-dialog .ant-modal-title > * {
    margin: 0;
    font-family: inherit;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.014em;
    line-height: 1.4;
    text-align: left;
    color: ${colors.gray[900]};
  }

  /* A line under the title saying what the dialog is for, so the body can get
     straight to the controls instead of opening with a paragraph. */
  .fina-dialog .fina-modal-description {
    margin: 3px 0 0;
    font-family: inherit;
    font-size: 12.5px;
    font-weight: 400;
    letter-spacing: 0;
    line-height: 1.45;
    text-align: left;
    color: ${colors.gray[600]};
  }

  .fina-dialog .ant-modal-body {
    /* A zero min-height is what lets it shrink: a flex item defaults to its
       content's height and would otherwise push the column straight past
       the cap it was just given. */
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 20px;
  }

  /* A sunk band, so the actions read as a footer rather than as more body. */
  .fina-dialog .ant-modal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${space.sm}px;
    margin: 0;
    padding: 13px 20px;
    border-top: 1px solid ${line.hairline};
    background: ${colors.gray[25]};
    text-align: right;
  }

  /* A footer wrapper brings its own page-level margins — 2em above and below,
     set when it sat in the middle of a body. In the band the band does the
     spacing. Its WIDTH is left alone: that belongs to the dialog. */
  .fina-dialog .ant-modal-footer > * {
    margin: 0 !important;
  }

  .fina-dialog .ant-modal-footer .ant-btn + .ant-btn {
    margin-left: 0;
  }

  /* A square of its own, in line with the head's padding — and emphatically
     not the filled red disc it was. Red is the alarm colour, and it was on
     the one control in the dialog that does nothing. */
  .fina-dialog .ant-modal-close {
    top: 15px;
    right: 20px;
    width: auto;
    height: auto;
    color: ${colors.gray[700]};
  }

  .fina-dialog .ant-modal-close-x {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 1px solid ${line.field};
    border-radius: ${radii.control}px;
    background: ${colors.gray[50]};
    color: ${colors.gray[700]};
    font-size: 13px;
    line-height: 1;
    transition: background 0.15s ease, color 0.15s ease;
  }

  .fina-dialog .ant-modal-close-x .anticon {
    padding: 0;
    border-radius: 0;
    background: none;
    color: inherit;
  }

  .fina-dialog .ant-modal-close:hover .ant-modal-close-x {
    background: ${colors.gray[100]};
    color: ${colors.gray[900]};
  }

  /* Fields inside a dialog.
     Scoped here rather than fixed at the form components, which the whole
     product shares — this is the dashboard's dialogs being brought onto the
     design system, not a rewrite of every form in the app.

     What they were: a label in full-strength accent blue, and an input ringed
     in the same accent at every moment of its life, resting or not. A field
     that shouts before it is touched leaves nothing to say when it is
     focused, and a form of them reads as a wall of blue. Ink labels, hairline
     fields, and the accent kept for the ring that marks the one field the
     cursor is in. */
  .fina-dialog .ant-modal-body label {
    font-size: 12.5px;
    font-weight: 500;
    letter-spacing: -0.005em;
    color: ${colors.gray[700]};
  }

  /* The label's text is a typography element INSIDE the label, carrying its
     own accent colour — so colouring the label alone changed nothing and the
     labels stayed blue. This is the element that actually paints. */
  .fina-dialog .ant-modal-body label .ant-typography {
    font-size: 12.5px;
    font-weight: 500;
    opacity: 1;
    color: ${colors.gray[700]};
  }


  /* A field in error says so at its edge as well as in words. */
  .fina-dialog .ant-modal-body .ant-input-number-status-error,
  .fina-dialog .ant-modal-body .ant-input-number[error] {
    border-color: ${colors.error[300]};
  }

  .fina-dialog .ant-modal-body .ant-input-number,
  .fina-dialog .ant-modal-body .ant-input,
  .fina-dialog .ant-modal-body .ant-input-affix-wrapper {
    width: 100%;
    border-radius: ${radii.control}px;
    border: 1px solid ${line.field};
    box-shadow: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  .fina-dialog .ant-modal-body .ant-input-number:hover,
  .fina-dialog .ant-modal-body .ant-input:hover,
  .fina-dialog .ant-modal-body .ant-input-affix-wrapper:hover {
    border-color: ${colors.gray[400]};
  }

  .fina-dialog .ant-modal-body .ant-input-number-focused,
  .fina-dialog .ant-modal-body .ant-input:focus,
  .fina-dialog .ant-modal-body .ant-input-affix-wrapper-focused {
    border-color: ${colors.primary[500]};
    box-shadow: ${shadow.focusRing};
  }

  .fina-dialog .ant-modal-body .ant-input-number-input {
    height: 38px;
    padding: 0 12px;
    font-size: 14px;
    color: ${colors.gray[900]};
  }

  /* Arriving. The library scales a dialog up from a FIFTH of its size — a
     440px dialog starts 88px wide — which is a lot of movement for something
     that is simply now there. A short rise out of 4% instead.

     The class is ant-zoom-*, not zoom-*: the library prefixes them, and
     without the prefix this matches nothing and the balloon still plays. */
  .fina-dialog .ant-modal.ant-zoom-enter,
  .fina-dialog .ant-modal.ant-zoom-appear {
    animation-name: finaDialogIn;
    animation-duration: 0.18s;
    animation-timing-function: cubic-bezier(0.22, 1, 0.36, 1);
  }

  .fina-dialog .ant-modal.ant-zoom-leave {
    animation-name: finaDialogOut;
    animation-duration: 0.12s;
    animation-timing-function: ease-in;
  }

  @keyframes finaDialogIn {
    from { opacity: 0; transform: scale(0.96); }
    to { opacity: 1; transform: none; }
  }

  @keyframes finaDialogOut {
    from { opacity: 1; transform: none; }
    to { opacity: 0; transform: scale(0.98); }
  }

  @media (prefers-reduced-motion: reduce) {
    .fina-dialog .ant-modal.ant-zoom-enter,
    .fina-dialog .ant-modal.ant-zoom-appear,
    .fina-dialog .ant-modal.ant-zoom-leave {
      animation: none;
    }
  }

  /* ── Messages ───────────────────────────────────────────────────────
     The toasts a failed request raises. They were the library's untouched
     default: a 2px corner under a grey drop shadow, with the text in the
     library's ink and only the icon carrying any sense of what had happened.
     Same surface as every other floating thing in the product, and an error
     says so in the product's own red rather than in #ff4d4f. */
  .ant-message-notice-content {
    padding: 10px 14px;
    border: 1px solid ${line.hairline};
    border-radius: ${radii.menu}px;
    background: ${colors.white};
    box-shadow: ${shadow.pop};
    font-size: 13px;
    color: ${colors.gray[800]};
  }

  .ant-message-error {
    color: ${colors.error[600]};
  }

  .ant-message-error .anticon {
    color: ${colors.error[600]};
  }

  .ant-message-success .anticon {
    color: ${colors.success[600]};
  }

  .ant-message-warning .anticon {
    color: ${colors.warning[600]};
  }

  /* Form-level errors, wherever the library renders its own. */
  .ant-form-item-explain-error {
    color: ${colors.error[600]};
    font-size: 12px;
    font-weight: 500;
  }

  /* ── Figures ────────────────────────────────────────────────────────
     The library sets a margin after a statistic's prefix, so every amount on
     the investment page read "$ 1,950" with the currency floating clear of
     its number — while the same amount in the dashboard's tables and cards
     read "$1,950". A currency symbol belongs against its figure.

     The decimals stay quieter than the whole, which is how the dashboard
     sets a headline figure. */
  .ant-statistic-content-prefix {
    margin-right: 0;
  }

  .ant-statistic-content-value-decimal {
    color: ${colors.gray[500]};
  }

  /* ── Scrollbars inside panels ───────────────────────────────────── */
  .ant-table-content,
  .ant-table-body,
  .ant-select-dropdown,
  .ant-pro-card-body,
  .antd-pro-charts-pie-legend {
    scrollbar-width: thin;
    scrollbar-color: ${colors.gray[300]} transparent;
  }
  .ant-table-content::-webkit-scrollbar,
  .ant-table-body::-webkit-scrollbar,
  .ant-select-dropdown::-webkit-scrollbar,
  .ant-pro-card-body::-webkit-scrollbar,
  .antd-pro-charts-pie-legend::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  .ant-table-content::-webkit-scrollbar-track,
  .ant-table-body::-webkit-scrollbar-track,
  .ant-select-dropdown::-webkit-scrollbar-track,
  .ant-pro-card-body::-webkit-scrollbar-track,
  .antd-pro-charts-pie-legend::-webkit-scrollbar-track {
    background: transparent;
  }
  .ant-table-content::-webkit-scrollbar-thumb,
  .ant-table-body::-webkit-scrollbar-thumb,
  .ant-select-dropdown::-webkit-scrollbar-thumb,
  .ant-pro-card-body::-webkit-scrollbar-thumb,
  .antd-pro-charts-pie-legend::-webkit-scrollbar-thumb {
    background: ${colors.gray[300]};
    border-radius: ${radii.pill}px;
  }
  .ant-table-content::-webkit-scrollbar-thumb:hover,
  .ant-table-body::-webkit-scrollbar-thumb:hover {
    background: ${colors.gray[400]};
  }

  /* The ring's tooltip row. The chart library builds its bubble in the DOM
     rather than in the drawing, so its contents are ours to write and ours
     to style — one line: the slice's colour, its name, its share. */
  .fina-slice-tip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 10px;
    font-size: 12px;
    white-space: nowrap;
    color: ${colors.gray[600]};
  }
  .fina-slice-tip i {
    width: 7px;
    height: 7px;
    flex: none;
    border-radius: 50%;
  }
  .fina-slice-tip b {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${colors.gray[900]};
  }
  /* The library paints its own panel behind ours. */
  .g2-tooltip {
    padding: 0 !important;
  }
`;

export default GlobalStyle;
